import { useState, useEffect } from "react";
import {
  saveTeam,
  updateTeam,
  addTeamMembers,
  removeTeamMembers,
  getTeamById,
  deleteTeam,
  restoreTeam,
  getDeletedTeamsByOrg,
} from "../Api/TeamMaster";
import { getTeamBaseByOrg } from "../Api/TeamMaster";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Card,
  Modal,
  Badge,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Sample14 from "../Components/Logo";
import { getUsersByOrganization } from "../Api/Auth";
import "./TeamMasterStyles.css";

const TeamMaster = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [mode, setMode] = useState("create");
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showDeletedTeams, setShowDeletedTeams] = useState(false);
  const [deletedTeams, setDeletedTeams] = useState([]);

  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const userId = userDetails?.userId || null;
  const orgId = userDetails?.organisationId;
  const userRoles = userDetails?.roles || [];
  
  // Check if user has Admin role
  const isAdmin = userRoles.includes("Admin");

  const [teamData, setTeamData] = useState({
    teamName: "",
    teamMembers: [],
    orgId: String(orgId) || "",
    teamMasterGUID: "",
  });

  console.log("Team Data teamMembers:", teamData);
  console.log("Current userId:", userId);
  console.log("User roles:", userRoles);
  console.log("Is Admin:", isAdmin);

  const [newMember, setNewMember] = useState("");

  // Array of vibrant colors for member avatars
  const avatarColors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7B731",
    "#FF6B35",
    "#A8E6CF",
    "#FF8C94",
    "#6C5CE7",
    "#00B894",
    "#E17055",
    "#0984E3",
    "#FDCB6E",
    "#E84393",
    "#6AB04C",
    "#F9CA24",
    "#EB4D4B",
  ];

  useEffect(() => {
    if (orgId) {
      fetchTeams();
      fetchDeletedTeams();
      fetchOrganizationUsers();
    }
  }, [orgId]);

  // Filter teams whenever search term or teams change
  useEffect(() => {
    const currentTeams = showDeletedTeams ? deletedTeams : teams;
    if (searchTerm.trim() === "") {
      setFilteredTeams(currentTeams);
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      const filtered = currentTeams.filter((team) => {
        if (team.teamName?.toLowerCase().includes(searchLower)) {
          return true;
        }
        if (team.teamId?.toLowerCase().includes(searchLower)) {
          return true;
        }
        const hasMatchingMember = team.teamMembers?.some((member) => {
          const memberName = getUserDisplayName(member.memberId).toLowerCase();
          return memberName.includes(searchLower);
        });
        if (hasMatchingMember) {
          return true;
        }
        return false;
      });
      setFilteredTeams(filtered);
    }
  }, [searchTerm, teams, deletedTeams, showDeletedTeams, organizationUsers]);

  // Transform the flat API response into structured teams with members
  const transformTeamData = (apiResponse, isDeleted = false) => {
    if (!Array.isArray(apiResponse)) return [];

    const teamMap = new Map();

    apiResponse.forEach((record) => {
      const teamId = record.teamId;

      if (!teamMap.has(teamId)) {
        teamMap.set(teamId, {
          teamId: record.teamId,
          teamName: record.teamName,
          teamMasterGUID: record.teamMasterGUID,
          orgId: record.orgId,
          orgName: record.orgName,
          createdOnDate: record.createdOnDate || record.createdOn_date,
          createdOnTime: record.createdOnTime || record.createdOn_time,
          teamMembers: [],
          teamMemberIds: [],
          isActive: isDeleted ? false : true,
        });
      }

      if (record.teamMemberId !== null && record.memberId !== null) {
        const team = teamMap.get(teamId);
        const memberId = String(record.memberId);

        if (!team.teamMemberIds.includes(memberId)) {
          team.teamMemberIds.push(memberId);
          team.teamMembers.push({
            teamMemberId: record.teamMemberId,
            memberId: memberId,
            teamMemberName: record.teamMemberName,
            active: record.active,
          });
        }
      }
    });

    return Array.from(teamMap.values());
  };

  const fetchTeams = async () => {
    try {
      if (orgId) {
        setLoading(true);
        const teamsData = await getTeamBaseByOrg(orgId);
        console.log("Raw active teams data:", teamsData);

        if (Array.isArray(teamsData)) {
          const structuredTeams = transformTeamData(teamsData, false);
          console.log("Structured active teams:", structuredTeams);
          setTeams(structuredTeams);
          if (!showDeletedTeams) {
            setFilteredTeams(structuredTeams);
          }
        } else {
          console.warn("Unexpected teams data format:", teamsData);
          setTeams([]);
          setFilteredTeams([]);
        }
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error(`Error fetching teams: ${error.message}`);
      setTeams([]);
      setFilteredTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedTeams = async () => {
    try {
      if (orgId) {
        const deletedTeamsData = await getDeletedTeamsByOrg(orgId);
        console.log("Raw deleted teams data:", deletedTeamsData);

        if (Array.isArray(deletedTeamsData)) {
          const structuredDeletedTeams = transformTeamData(deletedTeamsData, true);
          console.log("Structured deleted teams:", structuredDeletedTeams);
          setDeletedTeams(structuredDeletedTeams);
          if (showDeletedTeams) {
            setFilteredTeams(structuredDeletedTeams);
          }
        } else {
          setDeletedTeams([]);
          if (showDeletedTeams) {
            setFilteredTeams([]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching deleted teams:", error);
      setDeletedTeams([]);
      if (showDeletedTeams) {
        setFilteredTeams([]);
      }
    }
  };

  const fetchOrganizationUsers = async () => {
    try {
      if (orgId) {
        setUsersLoading(true);
        const response = await getUsersByOrganization(orgId);
        console.log("Organization users:", response);
        setOrganizationUsers(response.users || []);
      }
    } catch (error) {
      toast.error(`Error fetching organization users: ${error.message}`);
      setOrganizationUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle soft delete team
  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    
    if (!isAdmin) {
      toast.error("Only users with Admin role can delete teams");
      return;
    }

    setLoading(true);
    try {
      const result = await deleteTeam(selectedTeam.teamId, userId);
      if (result.success) {
        toast.success(result.message || "Team deleted successfully");
        setShowDeleteConfirmModal(false);
        await Promise.all([fetchTeams(), fetchDeletedTeams()]);
        setSelectedTeam(null);
      } else {
        toast.error(result.message || "Failed to delete team");
      }
    } catch (error) {
      toast.error(error.message || "Error deleting team");
    } finally {
      setLoading(false);
    }
  };

  // Handle restore team
  const handleRestoreTeam = async (team) => {
    if (!isAdmin) {
      toast.error("Only users with Admin role can restore teams");
      return;
    }

    setLoading(true);
    try {
      const result = await restoreTeam(team.teamId, userId);
      if (result.success) {
        toast.success(result.message || "Team restored successfully");
        await Promise.all([fetchTeams(), fetchDeletedTeams()]);
      } else {
        toast.error(result.message || "Failed to restore team");
      }
    } catch (error) {
      toast.error(error.message || "Error restoring team");
    } finally {
      setLoading(false);
    }
  };

  // Toggle between active and deleted teams
  const toggleShowDeletedTeams = async () => {
    const newShowDeleted = !showDeletedTeams;
    setShowDeletedTeams(newShowDeleted);
    
    if (newShowDeleted) {
      if (deletedTeams.length === 0) {
        await fetchDeletedTeams();
      } else {
        setFilteredTeams(deletedTeams);
      }
    } else {
      setFilteredTeams(teams);
    }
    setSearchTerm("");
  };

  // Get initials from username
  const getInitials = (username) => {
    if (!username) return "?";
    const names = username.trim().split(/\s+/);
    if (names.length === 1) {
      return names[0].charAt(0).toUpperCase();
    }
    return (
      names[0].charAt(0) + names[names.length - 1].charAt(0)
    ).toUpperCase();
  };

  // Generate consistent color based on memberId
  const getMemberColor = (memberId) => {
    const index = parseInt(memberId) % avatarColors.length;
    return avatarColors[index];
  };

  const handleChange = (e) => {
    setTeamData({ ...teamData, [e.target.name]: e.target.value });
  };

  const handleAddMember = (userId) => {
    console.log("Adding member with userId:", userId);
    const userIdString = String(userId);

    if (
      userIdString &&
      userIdString !== "undefined" &&
      !teamData.teamMembers.includes(userIdString)
    ) {
      setTeamData({
        ...teamData,
        teamMembers: [...teamData.teamMembers, userIdString],
      });
      setNewMember("");
    } else {
      console.log("Invalid userId or already exists:", userIdString);
    }
  };

  const handleRemoveMember = (memberToRemove) => {
    setTeamData({
      ...teamData,
      teamMembers: teamData.teamMembers.filter(
        (member) => member !== memberToRemove,
      ),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      toast.error("User not authenticated. Please login again.");
      return;
    }

    setLoading(true);

    console.log("Submitting team data:", teamData);
    console.log("Using userId:", userId);

    try {
      if (mode === "create") {
        await saveTeam(teamData, userId);
        toast.success("🦄 Team Created Successfully");
      } else {
        await updateTeam(selectedTeam.teamId, {
          teamName: teamData.teamName,
          orgId: String(teamData.orgId),
          userName: String(userId),
        });
        toast.success("🦄 Team Updated Successfully");
      }

      resetForm();
      setShowModal(false);
      await Promise.all([fetchTeams(), fetchDeletedTeams()]);
    } catch (error) {
      console.error("Error saving team:", error);
      toast.error(`🦄 Error saving team: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (team) => {
    if (!isAdmin) {
      toast.error("Only users with Admin role can edit teams");
      return;
    }
    
    setTeamData({
      teamName: team.teamName,
      teamMembers: team.teamMemberIds || [],
      orgId: team.orgId,
      teamMasterGUID: team.teamMasterGUID,
    });
    setSelectedTeam(team);
    setMode("edit");
    setShowModal(true);
  };

  const openDeleteConfirmModal = (team) => {
    if (!isAdmin) {
      toast.error("Only users with Admin role can delete teams");
      return;
    }
    setSelectedTeam(team);
    setShowDeleteConfirmModal(true);
  };

  const handleAddMembers = async () => {
    if (!newMember.trim() || !selectedTeam) {
      toast.error("Please select a team member");
      return;
    }

    if (!userId) {
      toast.error("User not authenticated");
      return;
    }

    try {
      const memberIdString = String(newMember.trim());
      await addTeamMembers(selectedTeam.teamId, [memberIdString], userId);
      toast.success("🦄 Team Member Added Successfully");
      setNewMember("");

      setSelectedTeam((prev) => ({
        ...prev,
        teamMembers: [
          ...(prev.teamMembers || []),
          {
            memberId: memberIdString,
            teamMemberName: getUserDisplayName(memberIdString),
          },
        ],
        teamMemberIds: [...(prev.teamMemberIds || []), memberIdString],
      }));

      await Promise.all([fetchTeams(), fetchDeletedTeams()]);
    } catch (error) {
      toast.error(`🦄 Error adding team member: ${error.message}`);
    }
  };

  const handleRemoveMemberFromTeam = async (memberId) => {
    if (!userId) {
      toast.error("User not authenticated");
      return;
    }

    try {
      await removeTeamMembers(selectedTeam.teamId, [memberId], userId);
      toast.success("🦄 Team Member Removed Successfully");

      setSelectedTeam((prev) => ({
        ...prev,
        teamMembers: (prev.teamMembers || []).filter(
          (member) => member.memberId !== memberId,
        ),
        teamMemberIds: (prev.teamMemberIds || []).filter(
          (id) => id !== memberId,
        ),
      }));

      await Promise.all([fetchTeams(), fetchDeletedTeams()]);
    } catch (error) {
      toast.error(`🦄 Error removing team member: ${error.message}`);
    }
  };

  const resetForm = () => {
    setTeamData({
      teamName: "",
      teamMembers: [],
      orgId: orgId || "",
      teamMasterGUID: "",
    });
    setNewMember("");
    setSelectedTeam(null);
    setMode("create");
  };

  const openMembersModal = (team) => {
    setSelectedTeam(team);
    setShowMembersModal(true);
  };

  const getUserDisplayName = (memberId) => {
    const user = organizationUsers.find(
      (u) => String(u.userId) === String(memberId),
    );
    return user ? user.username : `User ID: ${memberId}`;
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  // Member Avatar Component - shows name only on hover tooltip
  const MemberAvatar = ({
    memberId,
    name,
    size = "md",
    showRemove = false,
    onRemove = null,
  }) => {
    const displayName = name || getUserDisplayName(memberId);
    const initials = getInitials(displayName);
    const bgColor = getMemberColor(memberId);
    const sizeClass = size === "sm" ? "tmum-avatar-sm" : "tmum-avatar";

    return (
      <div className="tmum-member-avatar-container">
        <div
          className={`${sizeClass} tmum-member-avatar`}
          style={{ backgroundColor: bgColor }}
          data-name={displayName}
        >
          {initials}
          {showRemove && onRemove && (
            <span
              className="tmum-avatar-remove-icon"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              ×
            </span>
          )}
        </div>
        <div className="tmum-avatar-tooltip">{displayName}</div>
      </div>
    );
  };

  // More Members Counter Component with hover tooltip
  const MoreMembersCounter = ({ count, members }) => {
    return (
      <div className="tmum-more-members-container">
        <div className="tmum-more-members">+{count}</div>
        <div className="tmum-more-members-tooltip">
          {members.map((member, idx) => (
            <div key={idx} className="tmum-more-member-item">
              <div
                className="tmum-more-member-avatar"
                style={{ backgroundColor: getMemberColor(member.memberId) }}
              >
                {getInitials(
                  member.teamMemberName || getUserDisplayName(member.memberId),
                )}
              </div>
              <span className="tmum-more-member-name">
                {member.teamMemberName || getUserDisplayName(member.memberId)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="tmum-main-container">
      <Container fluid className="tmum-fields-container">
        <div className="tmum-header-section">
          <h2 className="tmum-title">Team Management</h2>
          <div className="tmum-header-buttons">
            <Button
              variant={showDeletedTeams ? "secondary" : "info"}
              onClick={toggleShowDeletedTeams}
              className="tmum-toggle-button"
            >
              {showDeletedTeams ? "Show Active Teams" : "Show Deleted Teams"}
            </Button>
            {!showDeletedTeams && (
              <Button
                variant="primary"
                onClick={() => setShowModal(true)}
                className="tmum-create-button"
                disabled={!isAdmin}
                title={!isAdmin ? "Only Admin can create teams" : ""}
              >
                Create New Team
              </Button>
            )}
          </div>
        </div>

        {/* Admin role indicator */}
        {!isAdmin && (
          <div className="tmum-warning-banner">
            <span>⚠️ You have view-only access. Admin privileges required for create, edit, and delete operations.</span>
          </div>
        )}

        {/* Search Bar */}
        <div className="tmum-search-section">
          <div className="tmum-search-container">
            <div className="tmum-search-icon">🔍</div>
            <input
              type="text"
              className="tmum-search-input"
              placeholder={`Search by team name, team ID, or member name...${showDeletedTeams ? ' (Deleted Teams)' : ''}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="tmum-search-clear" onClick={clearSearch}>
                ✕
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="tmum-search-results-info">
              Found {filteredTeams.length} team(s) matching "{searchTerm}"
            </div>
          )}
        </div>

        {loading && (
          <div className="tmum-loading-overlay">
            <Sample14 />
          </div>
        )}

        <Row>
          {filteredTeams.map((team) => (
            <Col md={6} lg={4} key={team.teamId} className="tmum-mb-4">
              <Card className={`tmum-team-card ${showDeletedTeams ? 'tmum-deleted-card' : ''}`}>
                <Card.Header className="tmum-card-header">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <h5 className="tmum-team-name mb-0">
                      {team.teamName}
                      {showDeletedTeams && (
                        <Badge className="tmum-deleted-badge ms-2">Deleted</Badge>
                      )}
                    </h5>
                    <Badge className="tmum-member-badge">
                      {team.teamMembers?.length || 0} members
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="tmum-card-body">
                  <p className="tmum-team-id">Team ID: {team.teamId}</p>
                  <div className="tmum-members-section">
                    <strong className="tmum-members-label">Members:</strong>
                    <div className="tmum-members-list">
                      {team.teamMembers?.slice(0, 6).map((member, index) => (
                        <MemberAvatar
                          key={index}
                          memberId={member.memberId}
                          name={member.teamMemberName}
                          size="sm"
                        />
                      ))}
                      {team.teamMembers?.length > 6 && (
                        <MoreMembersCounter
                          count={team.teamMembers.length - 6}
                          members={team.teamMembers.slice(6)}
                        />
                      )}
                      {(!team.teamMembers || team.teamMembers.length === 0) && (
                        <span className="tmum-no-members-text-small">
                          No members
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="tmum-created-info">
                    Created: {team.createdOnDate} {team.createdOnTime}
                  </p>
                </Card.Body>
                <Card.Footer className="tmum-card-footer">
                  <div className="tmum-button-group">
                    <Button
                      size="sm"
                      className="tmum-manage-button"
                      onClick={() => openMembersModal(team)}
                      disabled={showDeletedTeams}
                    >
                      Manage Members
                    </Button>
                    {!showDeletedTeams ? (
                      <>
                        <Button
                          size="sm"
                          className="tmum-edit-button"
                          onClick={() => handleEdit(team)}
                          disabled={!isAdmin}
                          title={!isAdmin ? "Only Admin can edit teams" : ""}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          className="tmum-delete-button"
                          onClick={() => openDeleteConfirmModal(team)}
                          disabled={!isAdmin}
                          title={!isAdmin ? "Only Admin can delete teams" : ""}
                        >
                          Delete
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="success"
                        className="tmum-restore-button"
                        onClick={() => handleRestoreTeam(team)}
                        disabled={!isAdmin}
                        title={!isAdmin ? "Only Admin can restore teams" : ""}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>

        {filteredTeams.length === 0 && !loading && (
          <div className="tmum-empty-state">
            {searchTerm ? (
              <>
                <h5 className="tmum-empty-title">No matching teams found</h5>
                <p className="tmum-empty-text">
                  No teams match "{searchTerm}". Try a different search term.
                </p>
                <Button variant="outline-primary" onClick={clearSearch}>
                  Clear Search
                </Button>
              </>
            ) : showDeletedTeams ? (
              <>
                <h5 className="tmum-empty-title">No deleted teams found</h5>
                <p className="tmum-empty-text">
                  There are no soft-deleted teams in this organization
                </p>
                <Button variant="outline-primary" onClick={toggleShowDeletedTeams}>
                  View Active Teams
                </Button>
              </>
            ) : (
              <>
                <h5 className="tmum-empty-title">No teams found</h5>
                <p className="tmum-empty-text">
                  Create your first team to get started
                </p>
              </>
            )}
          </div>
        )}

        {/* Create/Edit Team Modal */}
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            resetForm();
          }}
          size="lg"
          className="tmum-modal"
          contentClassName="tmum-modal-content"
        >
          <Modal.Header closeButton className="tmum-modal-header">
            <Modal.Title className="tmum-modal-title">
              {mode === "create" ? "Create New Team" : "Edit Team"}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body className="tmum-modal-body">
              <Row>
                <Col md={12}>
                  <Form.Group className="tmum-form-group">
                    <Form.Label className="tmum-form-label">
                      Team Name <span className="tmum-required-star">**</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="teamName"
                      value={teamData.teamName}
                      onChange={handleChange}
                      required
                      className="tmum-form-control"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="tmum-mt-3">
                <Col md={12}>
                  <Form.Group className="tmum-form-group">
                    <div className="tmum-selected-members-container">
                      {teamData.teamMembers.length === 0 ? (
                        <p className="tmum-no-members-text">
                          No members added yet
                        </p>
                      ) : (
                        teamData.teamMembers.map((memberId, index) => (
                          <MemberAvatar
                            key={index}
                            memberId={memberId}
                            name={getUserDisplayName(memberId)}
                            size="sm"
                            showRemove={true}
                            onRemove={() => handleRemoveMember(memberId)}
                          />
                        ))
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="tmum-modal-footer">
              <Button
                variant="secondary"
                className="tmum-cancel-button"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="tmum-submit-button"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : mode === "create"
                    ? "Create Team"
                    : "Update Team"}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Manage Members Modal */}
        <Modal
          show={showMembersModal}
          onHide={() => {
            setShowMembersModal(false);
            setNewMember("");
          }}
          size="md"
          className="tmum-modal"
          contentClassName="tmum-modal-content"
        >
          <Modal.Header closeButton className="tmum-modal-header">
            <Modal.Title className="tmum-modal-title">
              Manage Team Members - {selectedTeam?.teamName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="tmum-modal-body">
            <div className="tmum-add-member-section">
              <Form.Select
                value={newMember}
                onChange={(e) => setNewMember(e.target.value)}
                className="tmum-select-control"
                disabled={!isAdmin}
              >
                <option value="">Select a team member</option>
                {organizationUsers
                  .filter(
                    (user) =>
                      !selectedTeam?.teamMemberIds?.includes(
                        String(user.userId),
                      ),
                  )
                  .map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.username} (ID: {user.userId})
                    </option>
                  ))}
              </Form.Select>
              <Button
                variant="outline-primary"
                className="tmum-add-member-button"
                onClick={handleAddMembers}
                disabled={!newMember || !isAdmin}
              >
                Add Member
              </Button>
            </div>

            <h6 className="tmum-section-title">
              Current Members ({selectedTeam?.teamMembers?.length || 0})
            </h6>
            <div className="tmum-members-list-container">
              {selectedTeam?.teamMembers?.map((member, index) => (
                <div key={index} className="tmum-member-item">
                  <MemberAvatar
                    memberId={member.memberId}
                    name={member.teamMemberName}
                    size="md"
                  />
                  <span className="tmum-member-name">
                    {getUserDisplayName(member.memberId)}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="tmum-remove-member-button"
                    onClick={() => handleRemoveMemberFromTeam(member.memberId)}
                    disabled={!isAdmin}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {(!selectedTeam?.teamMembers ||
                selectedTeam.teamMembers.length === 0) && (
                <p className="tmum-no-members-text">No members in this team</p>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="tmum-modal-footer">
            <Button
              variant="secondary"
              className="tmum-cancel-button"
              onClick={() => {
                setShowMembersModal(false);
                setNewMember("");
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteConfirmModal}
          onHide={() => setShowDeleteConfirmModal(false)}
          size="sm"
          className="tmum-modal"
          contentClassName="tmum-modal-content"
        >
          <Modal.Header closeButton className="tmum-modal-header bg-danger text-white">
            <Modal.Title className="tmum-modal-title">Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body className="tmum-modal-body">
            <p>Are you sure you want to delete team <strong>"{selectedTeam?.teamName}"</strong>?</p>
            <p className="text-muted small">This action can be undone by restoring the team.</p>
          </Modal.Body>
          <Modal.Footer className="tmum-modal-footer">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteTeam}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Yes, Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default TeamMaster;

// import { useState, useEffect } from "react";
// import {
//   saveTeam,
//   updateTeam,
//   addTeamMembers,
//   removeTeamMembers,
//   getTeamsByOrg,
//   getTeamById,
// } from "../Api/TeamMaster";
// import {
//   Container,
//   Form,
//   Button,
//   Row,
//   Col,
//   Card,
//   Modal,
//   Badge,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import { useNavigate } from "react-router-dom";
// import Sample14 from "../Components/Logo";
// import { getUsersByOrganization } from "../Api/Auth";
// import "./TeamMasterStyles.css"; // Import the unique CSS

// const TeamMaster = () => {
//   const navigate = useNavigate();
//   const [loading, setLoading] = useState(false);
//   const [teams, setTeams] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [showMembersModal, setShowMembersModal] = useState(false);
//   const [selectedTeam, setSelectedTeam] = useState(null);
//   const [mode, setMode] = useState("create");
//   const [organizationUsers, setOrganizationUsers] = useState([]);
//   const [usersLoading, setUsersLoading] = useState(false);

//   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
//   const userId = userDetails?.userId || null;
//   const orgId = userDetails?.organisationId;

//   const [teamData, setTeamData] = useState({
//     teamName: "",
//     teamMembers: [],
//     orgId: String(orgId) || "",
//   });

//   console.log("Team Data teamMembers:", teamData);
//   console.log("Current userId:", userId);

//   const [newMember, setNewMember] = useState("");

//   useEffect(() => {
//     if (orgId) {
//       fetchTeams();
//       fetchOrganizationUsers();
//     }
//   }, [orgId]);

//   const fetchTeams = async () => {
//     try {
//       if (orgId) {
//         setLoading(true);
//         const teamsData = await getTeamsByOrg(orgId);
//         console.log("teamsData", teamsData);
//         console.log("Is teamsData an array?", Array.isArray(teamsData));

//         if (Array.isArray(teamsData)) {
//           setTeams(teamsData);
//         } else if (
//           teamsData &&
//           teamsData.data &&
//           Array.isArray(teamsData.data)
//         ) {
//           setTeams(teamsData.data);
//         } else {
//           console.warn("Unexpected teams data format:", teamsData);
//           setTeams([]);
//         }
//       }
//     } catch (error) {
//       console.error("Error fetching teams:", error);
//       toast.error(`Error fetching teams: ${error.message}`);
//       setTeams([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchOrganizationUsers = async () => {
//     try {
//       if (orgId) {
//         setUsersLoading(true);
//         const response = await getUsersByOrganization(orgId);
//         console.log("Organization users:", response);
//         setOrganizationUsers(response.users || []);
//       }
//     } catch (error) {
//       toast.error(`Error fetching organization users: ${error.message}`);
//       setOrganizationUsers([]);
//     } finally {
//       setUsersLoading(false);
//     }
//   };

//   const handleChange = (e) => {
//     setTeamData({ ...teamData, [e.target.name]: e.target.value });
//   };

//   const handleAddMember = (userId) => {
//     console.log("Adding member with userId:", userId, "Type:", typeof userId);
//     const userIdString = String(userId);
//     console.log("Converted userId:", userIdString);

//     if (
//       userIdString &&
//       userIdString !== "undefined" &&
//       !teamData.teamMembers.includes(userIdString)
//     ) {
//       setTeamData({
//         ...teamData,
//         teamMembers: [...teamData.teamMembers, userIdString],
//       });
//       setNewMember("");
//     } else {
//       console.log("Invalid userId or already exists:", userIdString);
//     }
//   };

//   const handleRemoveMember = (memberToRemove) => {
//     setTeamData({
//       ...teamData,
//       teamMembers: teamData.teamMembers.filter(
//         (member) => member !== memberToRemove,
//       ),
//     });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!userId) {
//       toast.error("User not authenticated. Please login again.");
//       return;
//     }

//     setLoading(true);

//     console.log("Submitting team data:", teamData);
//     console.log("Using userId:", userId);

//     try {
//       if (mode === "create") {
//         await saveTeam(teamData, userId);
//         toast.success("🦄 Team Created Successfully");
//       } else {
//         await updateTeam(selectedTeam.teamId, teamData, userId);
//         toast.success("🦄 Team Updated Successfully");
//       }

//       resetForm();
//       setShowModal(false);
//       fetchTeams();
//     } catch (error) {
//       console.error("Error saving team:", error);
//       toast.error(`🦄 Error saving team: ${error.message || error}`);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEdit = (team) => {
//     setTeamData({
//       teamName: team.teamName,
//       teamMembers: team.teamMembers || [],
//       orgId: team.orgId,
//     });
//     setSelectedTeam(team);
//     setMode("edit");
//     setShowModal(true);
//   };

//   const handleAddMembers = async () => {
//     if (!newMember.trim() || !selectedTeam) {
//       toast.error("Please select a team member");
//       return;
//     }

//     if (!userId) {
//       toast.error("User not authenticated");
//       return;
//     }

//     try {
//       const memberIdString = String(newMember.trim());
//       await addTeamMembers(selectedTeam.teamId, [memberIdString], userId);
//       toast.success("🦄 Team Member Added Successfully");
//       setNewMember("");

//       setSelectedTeam((prev) => ({
//         ...prev,
//         teamMembers: [...(prev.teamMembers || []), memberIdString],
//       }));

//       fetchTeams();
//     } catch (error) {
//       toast.error(`🦄 Error adding team member: ${error.message}`);
//     }
//   };

//   const handleRemoveMemberFromTeam = async (memberId) => {
//     if (!userId) {
//       toast.error("User not authenticated");
//       return;
//     }

//     try {
//       await removeTeamMembers(selectedTeam.teamId, [memberId], userId);
//       toast.success("🦄 Team Member Removed Successfully");

//       setSelectedTeam((prev) => ({
//         ...prev,
//         teamMembers: (prev.teamMembers || []).filter(
//           (member) => member !== memberId,
//         ),
//       }));

//       fetchTeams();
//     } catch (error) {
//       toast.error(`🦄 Error removing team member: ${error.message}`);
//     }
//   };

//   const resetForm = () => {
//     setTeamData({
//       teamName: "",
//       teamMembers: [],
//       orgId: orgId || "",
//     });
//     setNewMember("");
//     setSelectedTeam(null);
//     setMode("create");
//   };

//   const openMembersModal = (team) => {
//     setSelectedTeam(team);
//     setShowMembersModal(true);
//   };

//   const getUserDisplayName = (memberId) => {
//     const user = organizationUsers.find((u) => String(u.userId) === memberId);
//     return user ? user.username : `User ID: ${memberId}`;
//   };

//   return (
//     <div className="tmum-main-container">
//       <Container fluid className="tmum-fields-container">
//         <div className="tmum-header-section">
//           <h2 className="tmum-title">Team Management</h2>
//           <Button
//             variant="primary"
//             onClick={() => setShowModal(true)}
//             className="tmum-create-button"
//           >
//             Create New Team
//           </Button>
//         </div>

//         {loading && (
//           <div className="tmum-loading-overlay">
//             <Sample14 />
//           </div>
//         )}

//         <Row>
//           {teams.map((team) => (
//             <Col md={6} lg={4} key={team.teamId} className="tmum-mb-4">
//               <Card className="tmum-team-card">
//                 <Card.Header className="tmum-card-header">
//                   <div className="d-flex justify-content-between align-items-center w-100">
//                     <h5 className="tmum-team-name mb-0">{team.teamName}</h5>
//                     <Badge className="tmum-member-badge">
//                       {team.teamMembers?.length || 0} members
//                     </Badge>
//                   </div>
//                 </Card.Header>
//                 <Card.Body className="tmum-card-body">
//                   <p className="tmum-team-id">Team ID: {team.teamId}</p>
//                   <div className="tmum-members-section">
//                     <strong className="tmum-members-label">Members:</strong>
//                     <div className="tmum-members-list">
//                       {team.teamMembers?.slice(0, 3).map((memberId, index) => (
//                         <Badge key={index} className="tmum-member-badge-sm">
//                           {getUserDisplayName(memberId)}
//                         </Badge>
//                       ))}
//                       {team.teamMembers?.length > 3 && (
//                         <Badge className="tmum-more-badge">
//                           +{team.teamMembers.length - 3} more
//                         </Badge>
//                       )}
//                       {(!team.teamMembers || team.teamMembers.length === 0) && (
//                         <span className="tmum-no-members-text-small">
//                           No members
//                         </span>
//                       )}
//                     </div>
//                   </div>
//                   <p className="tmum-created-info">
//                     Created: {team.createdOnDate} by {team.createdBy}
//                   </p>
//                 </Card.Body>
//                 <Card.Footer className="tmum-card-footer">
//                   <div className="tmum-button-group">
//                     <Button
//                       size="sm"
//                       className="tmum-manage-button"
//                       onClick={() => openMembersModal(team)}
//                     >
//                       Manage Members
//                     </Button>
//                     <Button
//                       size="sm"
//                       className="tmum-edit-button"
//                       onClick={() => handleEdit(team)}
//                     >
//                       Edit
//                     </Button>
//                   </div>
//                 </Card.Footer>
//               </Card>
//             </Col>
//           ))}
//         </Row>

//         {teams.length === 0 && !loading && (
//           <div className="tmum-empty-state">
//             <h5 className="tmum-empty-title">No teams found</h5>
//             <p className="tmum-empty-text">
//               Create your first team to get started
//             </p>
//           </div>
//         )}

//         {/* Create/Edit Team Modal */}
//         <Modal
//           show={showModal}
//           onHide={() => {
//             setShowModal(false);
//             resetForm();
//           }}
//           size="lg"
//           className="tmum-modal"
//           contentClassName="tmum-modal-content"
//         >
//           <Modal.Header closeButton className="tmum-modal-header">
//             <Modal.Title className="tmum-modal-title">
//               {mode === "create" ? "Create New Team" : "Edit Team"}
//             </Modal.Title>
//           </Modal.Header>
//           <Form onSubmit={handleSubmit}>
//             <Modal.Body className="tmum-modal-body">
//               <Row>
//                 <Col md={12}>
//                   <Form.Group className="tmum-form-group">
//                     <Form.Label className="tmum-form-label">
//                       Team Name <span className="tmum-required-star">**</span>
//                     </Form.Label>
//                     <Form.Control
//                       type="text"
//                       name="teamName"
//                       value={teamData.teamName}
//                       onChange={handleChange}
//                       required
//                       className="tmum-form-control"
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>

//               <Row className="tmum-mt-3">
//                 <Col md={12}>
//                   <Form.Group className="tmum-form-group">
//                     <Form.Label className="tmum-form-label">
//                       Team Members (Optional)
//                     </Form.Label>

//                     <div className="tmum-add-member-section">
//                       <Form.Select
//                         value={newMember}
//                         onChange={(e) => setNewMember(e.target.value)}
//                         className="tmum-select-control"
//                       >
//                         <option value="">Select a team member</option>
//                         {organizationUsers
//                           .filter(
//                             (user) =>
//                               !teamData.teamMembers.includes(
//                                 String(user.userId),
//                               ),
//                           )
//                           .map((user) => (
//                             <option key={user.userId} value={user.userId}>
//                               {user.username} (ID: {user.userId})
//                             </option>
//                           ))}
//                       </Form.Select>
//                       <Button
//                         variant="outline-primary"
//                         className="tmum-add-member-button"
//                         onClick={() => handleAddMember(newMember)}
//                         disabled={!newMember}
//                       >
//                         Add Member
//                       </Button>
//                     </div>

//                     <div className="tmum-selected-members-container">
//                       {teamData.teamMembers.length === 0 ? (
//                         <p className="tmum-no-members-text">
//                           No members added yet
//                         </p>
//                       ) : (
//                         teamData.teamMembers.map((memberId, index) => (
//                           <span
//                             key={index}
//                             className="tmum-selected-member-badge"
//                           >
//                             {getUserDisplayName(memberId)}
//                             <span
//                               className="tmum-remove-member-icon"
//                               onClick={() => handleRemoveMember(memberId)}
//                             >
//                               ×
//                             </span>
//                           </span>
//                         ))
//                       )}
//                     </div>
//                   </Form.Group>
//                 </Col>
//               </Row>
//             </Modal.Body>
//             <Modal.Footer className="tmum-modal-footer">
//               <Button
//                 variant="secondary"
//                 className="tmum-cancel-button"
//                 onClick={() => {
//                   setShowModal(false);
//                   resetForm();
//                 }}
//               >
//                 Cancel
//               </Button>
//               <Button
//                 variant="primary"
//                 type="submit"
//                 className="tmum-submit-button"
//                 disabled={loading}
//               >
//                 {loading
//                   ? "Saving..."
//                   : mode === "create"
//                     ? "Create Team"
//                     : "Update Team"}
//               </Button>
//             </Modal.Footer>
//           </Form>
//         </Modal>

//         {/* Manage Members Modal */}
//         <Modal
//           show={showMembersModal}
//           onHide={() => {
//             setShowMembersModal(false);
//             setNewMember("");
//           }}
//           size="md"
//           className="tmum-modal"
//           contentClassName="tmum-modal-content"
//         >
//           <Modal.Header closeButton className="tmum-modal-header">
//             <Modal.Title className="tmum-modal-title">
//               Manage Team Members - {selectedTeam?.teamName}
//             </Modal.Title>
//           </Modal.Header>
//           <Modal.Body className="tmum-modal-body">
//             <div className="tmum-add-member-section">
//               <Form.Select
//                 value={newMember}
//                 onChange={(e) => setNewMember(e.target.value)}
//                 className="tmum-select-control"
//               >
//                 <option value="">Select a team member</option>
//                 {organizationUsers
//                   .filter(
//                     (user) =>
//                       !selectedTeam?.teamMembers?.includes(String(user.userId)),
//                   )
//                   .map((user) => (
//                     <option key={user.userId} value={user.userId}>
//                       {user.username} (ID: {user.userId})
//                     </option>
//                   ))}
//               </Form.Select>
//               <Button
//                 variant="outline-primary"
//                 className="tmum-add-member-button"
//                 onClick={handleAddMembers}
//                 disabled={!newMember}
//               >
//                 Add Member
//               </Button>
//             </div>

//             <h6 className="tmum-section-title">
//               Current Members ({selectedTeam?.teamMembers?.length || 0})
//             </h6>
//             <div className="tmum-members-list-container">
//               {selectedTeam?.teamMembers?.map((memberId, index) => (
//                 <div key={index} className="tmum-member-item">
//                   <span className="tmum-member-name">
//                     {getUserDisplayName(memberId)}
//                   </span>
//                   <Button
//                     size="sm"
//                     variant="outline-danger"
//                     className="tmum-remove-member-button"
//                     onClick={() => handleRemoveMemberFromTeam(memberId)}
//                   >
//                     Remove
//                   </Button>
//                 </div>
//               ))}
//               {(!selectedTeam?.teamMembers ||
//                 selectedTeam.teamMembers.length === 0) && (
//                 <p className="tmum-no-members-text">No members in this team</p>
//               )}
//             </div>
//           </Modal.Body>
//           <Modal.Footer className="tmum-modal-footer">
//             <Button
//               variant="secondary"
//               className="tmum-cancel-button"
//               onClick={() => {
//                 setShowMembersModal(false);
//                 setNewMember("");
//               }}
//             >
//               Close
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </div>
//   );
// };

// export default TeamMaster;
